import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Chip,
  Popover,
  Stack,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import sh from "bundles/common/utils/sh";
import DateTimeSelect from "bundles/Internal/components/common/DateTimeSelect";

export const EMPTY_FILTERS = {
  companyId: null,
  profilePipelineId: null,
  internalUserId: null,
  fromDate: null,
  toDate: null,
};

const chipSx = (active) => ({
  fontWeight: 600,
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  fontSize: 13,
  borderRadius: "0.6rem",
  height: 34,
  px: 0.5,
  cursor: "pointer",
  transition: "opacity 0.15s ease",
  ...(active
    ? {
      backgroundColor: "hsla(150, 38%, 17%, 0.08)",
      color: "hsl(150 38% 17%)",
      borderColor: "hsl(150 38% 17%)",
      "&:hover": { opacity: 0.8 },
    }
    : {
      backgroundColor: "#fff",
      color: "hsl(150 30% 12%)",
      borderColor: "hsl(140 8% 88%)",
      "&:hover": {
        borderColor: "hsl(140 8% 82%)",
      },
    }),
});

const TimeProjectTableFilters = ({ filters, onFiltersChange }) => {
  const [anchors, setAnchors] = useState({ date: null, company: null, pipeline: null, user: null });
  const [companies, setCompanies] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [companyInput, setCompanyInput] = useState("");
  const [pipelineInput, setPipelineInput] = useState("");
  const [userInput, setUserInput] = useState("");

  const companyDebounce = useRef(null);
  const pipelineDebounce = useRef(null);

  useEffect(() => {
    clearTimeout(companyDebounce.current);
    companyDebounce.current = setTimeout(() => {
      setLoadingCompanies(true);
      sh.get("/internal/search/companies", { params: { q: companyInput, limit: 20 } })
        .then((res) => {
          const data = res.data?.data || res.data;
          setCompanies(Array.isArray(data) ? data : []);
        })
        .catch(() => setCompanies([]))
        .finally(() => setLoadingCompanies(false));
    }, 250);
    return () => clearTimeout(companyDebounce.current);
  }, [companyInput]);

  useEffect(() => {
    clearTimeout(pipelineDebounce.current);
    pipelineDebounce.current = setTimeout(() => {
      setLoadingPipelines(true);
      sh.get("/internal/search/profile_pipelines", { params: { q: pipelineInput, limit: 20 } })
        .then((res) => setPipelines(Array.isArray(res.data) ? res.data : []))
        .catch(() => setPipelines([]))
        .finally(() => setLoadingPipelines(false));
    }, 250);
    return () => clearTimeout(pipelineDebounce.current);
  }, [pipelineInput]);

  useEffect(() => {
    setLoadingUsers(true);
    sh.get("/internal/search/internal_users", { params: { all: true } })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.internal_users || [];
        setUsers(data);
      })
      .catch(() => setUsers([]))
      .finally(() => setLoadingUsers(false));
  }, []);

  const openAnchor = (key, event) => setAnchors((prev) => ({ ...prev, [key]: event.currentTarget }));
  const closeAnchor = (key) => setAnchors((prev) => ({ ...prev, [key]: null }));

  const handleCompanyChange = useCallback((_, value) => {
    onFiltersChange({ ...filters, companyId: value?.id || null });
    closeAnchor("company");
  }, [filters, onFiltersChange]);

  const handlePipelineChange = useCallback((_, value) => {
    onFiltersChange({ ...filters, profilePipelineId: value?.id || null });
    closeAnchor("pipeline");
  }, [filters, onFiltersChange]);

  const handleUserChange = useCallback((_, value) => {
    onFiltersChange({ ...filters, internalUserId: value?.id || null });
    closeAnchor("user");
  }, [filters, onFiltersChange]);

  const handleClearAll = useCallback(() => {
    onFiltersChange({ ...EMPTY_FILTERS });
  }, [onFiltersChange]);

  const selectedCompany = companies.find((c) => c.id === filters.companyId);
  const selectedPipeline = pipelines.find((p) => p.id === filters.profilePipelineId);
  const selectedUser = users.find((u) => u.id === filters.internalUserId);

  const hasActiveFilters = filters.companyId || filters.profilePipelineId || filters.internalUserId || filters.fromDate || filters.toDate;

  return (
    <Box
      sx={{
        px: 3,
        py: 1,
        backgroundColor: "#fff",
        borderBottom: "1px solid hsl(140 8% 88%)",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <FilterListIcon sx={{ fontSize: 18, color: "hsl(150 30% 12%)", opacity: 0.5, mr: 0.5 }} />

        <Chip
          variant="outlined"
          label={filters.fromDate || filters.toDate ? `${filters.fromDate || "..."} — ${filters.toDate || "..."}` : "Date range"}
          onClick={(e) => openAnchor("date", e)}
          sx={chipSx(Boolean(filters.fromDate || filters.toDate))}
        />

        <Chip
          variant="outlined"
          label={selectedCompany ? selectedCompany.name : "Company"}
          onClick={(e) => openAnchor("company", e)}
          sx={chipSx(Boolean(filters.companyId))}
        />

        <Chip
          variant="outlined"
          label={selectedPipeline ? selectedPipeline.title : "Pipeline"}
          onClick={(e) => openAnchor("pipeline", e)}
          sx={chipSx(Boolean(filters.profilePipelineId))}
        />

        <Chip
          variant="outlined"
          label={selectedUser ? selectedUser.full_name : "User"}
          onClick={(e) => openAnchor("user", e)}
          sx={chipSx(Boolean(filters.internalUserId))}
        />

        {hasActiveFilters && (
          <Chip
            size="small"
            label="Clear all"
            deleteIcon={<CloseIcon sx={{ fontSize: "14px !important", color: "hsl(0 72% 51%) !important" }} />}
            onDelete={handleClearAll}
            onClick={handleClearAll}
            sx={{
              ml: 0.5,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              fontSize: 12,
              color: "hsl(0 72% 51%)",
              borderColor: "hsl(0 72% 51%)",
              backgroundColor: "transparent",
              height: 28,
              borderRadius: "0.6rem",
              transition: "opacity 0.15s ease",
              "&:hover": { backgroundColor: "hsla(0, 72%, 51%, 0.06)", opacity: 0.9 },
            }}
            variant="outlined"
          />
        )}
      </Stack>

      <Popover
        open={Boolean(anchors.date)}
        anchorEl={anchors.date}
        onClose={() => closeAnchor("date")}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: "0.75rem",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              minWidth: 320,
            },
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 13, mb: 1.5 }}>
            Date range
          </Typography>
          <Stack direction="row" spacing={1.5} mb={1.5} alignItems="center">
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 11, color: "rgba(42,51,78,0.6)", mb: 0.5 }}>From</Typography>
              <DateTimeSelect
                value={filters.fromDate || ""}
                onChange={(val) => onFiltersChange({ ...filters, fromDate: val || null })}
                placeholder="Start date"
                dateOnly
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 11, color: "rgba(42,51,78,0.6)", mb: 0.5 }}>To</Typography>
              <DateTimeSelect
                value={filters.toDate || ""}
                onChange={(val) => onFiltersChange({ ...filters, toDate: val || null })}
                placeholder="End date"
                dateOnly
              />
            </Box>
          </Stack>
          <Chip
            label="Clear"
            onClick={() => {
              onFiltersChange({ ...filters, fromDate: null, toDate: null });
              closeAnchor("date");
            }}
            sx={{ cursor: "pointer", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 12, borderRadius: "0.5rem" }}
          />
        </Box>
      </Popover>

      <Popover
        open={Boolean(anchors.company)}
        anchorEl={anchors.company}
        onClose={() => closeAnchor("company")}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: "0.75rem",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            },
          },
        }}
      >
        <Box sx={{ p: 2, width: 450 }}>
          <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 13, mb: 1.5 }}>
            Filter by company
          </Typography>
          <Autocomplete
            options={companies}
            getOptionLabel={(o) => o.name || ""}
            value={selectedCompany || null}
            inputValue={companyInput}
            onInputChange={(_, v, reason) => { if (reason === "input") setCompanyInput(v); }}
            onChange={handleCompanyChange}
            loading={loadingCompanies}
            size="small"
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search companies..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCompanies && <CircularProgress color="inherit" size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      </Popover>

      <Popover
        open={Boolean(anchors.pipeline)}
        anchorEl={anchors.pipeline}
        onClose={() => closeAnchor("pipeline")}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: "0.75rem",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            },
          },
        }}
      >
        <Box sx={{ p: 2, width: 450 }}>
          <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 13, mb: 1.5 }}>
            Filter by pipeline
          </Typography>
          <Autocomplete
            options={pipelines}
            getOptionLabel={(o) => o.title || ""}
            value={selectedPipeline || null}
            inputValue={pipelineInput}
            onInputChange={(_, v) => setPipelineInput(v)}
            onChange={handlePipelineChange}
            loading={loadingPipelines}
            size="small"
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search pipelines..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingPipelines && <CircularProgress color="inherit" size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      </Popover>

      <Popover
        open={Boolean(anchors.user)}
        anchorEl={anchors.user}
        onClose={() => closeAnchor("user")}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: "0.75rem",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            },
          },
        }}
      >
        <Box sx={{ p: 2, width: 450 }}>
          <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 13, mb: 1.5 }}>
            Filter by user
          </Typography>
          <Autocomplete
            options={users}
            getOptionLabel={(o) => o.full_name || ""}
            value={selectedUser || null}
            inputValue={userInput}
            onInputChange={(_, v) => setUserInput(v)}
            onChange={handleUserChange}
            loading={loadingUsers}
            size="small"
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search users..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingUsers && <CircularProgress color="inherit" size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      </Popover>
    </Box>
  );
};

export default TimeProjectTableFilters;
